import { logGreyError } from 'owa-analytics';
import { isClientId, convertRestIdToEwsId } from 'owa-identifiers';
import getModuleContextMailboxInfo from 'owa-module-context-mailboxinfo/lib/selectors/getModuleContextMailboxInfo';

// Note: these do not distinguish between REST and EWS concatenated ids.
// The only difference is the encoding.  REST ids are EWS ids, except they replace + with - and / with _.
export type IdType =
    | 'ConcatenatedImmutableId'
    | 'ConcatenatedEntryId'
    | 'StoreObjectImmutableId'
    | 'ClientId'
    | 'UnknownId';

// EWS encodes its IDs as base64 encoded ConcatenatedIds . The second byte of a ConcatenatedId is the IdStorageType.
// So, you can decode the base64 and look at the second byte of an EWS id. If it's a 9, then the id is an immutable id.
// If it's something else, it's a different IdStorageType.
// REST ids are similar to EWS ids, except they replace + with - and / with _.
// CAPIv3 (and maybe other services) are raw store IDs encoded as base64, sometimes with a spurious trailing 0.
// The first byte of a raw store ID is the id length.  If that is 46, then it's either an immutable id or a folder id.  If it's immutable, the 20th byte in the ID will also be 13.
export function getIdType(id: string): IdType {
    try {
        return parseIdType(id);
    } catch (e) {
        // unexpected since these are coming from the server, but assume a malformed id is not immutable
        // Using getModuleContextMailboxInfo: This is called within module context.
        // Falls back to global settings if no module context is available.
        const errorObj = e instanceof Error ? e : undefined;
        /* eslint-disable-next-line owa-custom-rules/telemetry-event-name-format -- (https://aka.ms/OWALintWiki)
         * Baseline. DO NOT COPY AND PASTE! Grandfathering existing event names; Devs who touch this file should update Event Names to follow Outlook Telemetry Naming Standards
         *	> Invalid Outlook telemetry EventName "getIdType": must start with a capital letter (A–Z). */
        logGreyError('getIdType', getModuleContextMailboxInfo(), errorObj, {
            reason: getInvalidBase64Reason(id),
        });
    }

    return 'UnknownId';
}

/** Classifies persisted IDs without emitting one error per malformed row. */
export function tryGetIdType(id: string): IdType {
    try {
        return parseIdType(id);
    } catch (error) {
        if (error instanceof DOMException && error.name === 'InvalidCharacterError') {
            return 'UnknownId';
        }
        throw error;
    }
}

function parseIdType(id: string): IdType {
    if (!id) {
        return 'UnknownId';
    }
    if (isClientId(id)) {
        return 'ClientId';
    }
    if (id.length % 4 === 1 && id.endsWith('0')) {
        id = id.slice(0, -1);
    }

    const bytes = Uint8Array.from(atob(convertRestIdToEwsId(id)), c => c.charCodeAt(0));
    if (bytes[0] === 46 && bytes[21] === 13) {
        return 'StoreObjectImmutableId';
    }
    if (bytes[0] === 0 || bytes[0] === 1) {
        if (bytes[1] === 9) {
            return 'ConcatenatedImmutableId';
        }
        if (bytes[1] === 3) {
            return 'ConcatenatedEntryId';
        }
    }
    return 'UnknownId';
}

export function getInvalidBase64Reason(id: string | undefined | null): string {
    let reason = 'unknown';

    try {
        if (!id) {
            reason = 'id is null or undefined';
        } else if (typeof id !== 'string') {
            reason = `id is not a string: ${typeof id}`;
        } else if (id.length % 4 !== 0) {
            reason = `id length is not a multiple of 4: ${id.length}. ${id.slice(
                0,
                4
            )}...${id.slice(-4)}`;
        } else {
            for (let i = 0; i < id.length; i++) {
                if (isNotBase64Char(id[i])) {
                    reason = `id contains invalid character at index ${i}: ${id[i]}`;
                    break;
                }
            }
        }
    } catch (e) {
        // Errors resulting from enabling useUnknownInCatchVariables
        // TS18046: 'e' is of type 'unknown'.
        // @ts-expect-error
        reason = `unexpected error: ${e.message}`;
    }

    return reason;
}

function isNotBase64Char(char: string): boolean {
    return !/[A-Za-z0-9+/=]/.test(char);
}
